import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const SHADER_SOURCE = 'https://www.khronos.org/webgl/extension/WEBGL_debug_shaders/';

/**
 * Two WebGL-derived signals the reference site collects and we did not.
 *
 * 1. webglFingerprint — actually RENDER a scene and hash the pixels, rather
 *    than only reading parameters. Parameter values are easy to spoof by
 *    overriding getParameter; rendered pixels are much harder, because a
 *    spoofed renderer string with a mismatched real pipeline produces different
 *    output than a genuine GPU.
 *
 * 2. webglTranslatedShaderLang — detect whether the browser translates GLSL to
 *    HLSL or MSL before handing it to the driver (a D3D/Metal ANGLE backend
 *    does; a native GL backend reports GLSL unchanged). Reading the translated
 *    source is a backend differentiator independent of the renderer string.
 */
export const webglRenderProbe: Probe = {
  id: 'webglRender',
  label: 'WebGL render & shader backend',
  layer: 'gpu',
  tier: 'client',
  category: 'hardware',
  description: 'GPU pixel-output hash and GLSL translation detection',
  sources: [SHADER_SOURCE],
  budgetMs: 4000,

  async run(): Promise<ProbeResult> {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 60;
    const gl = (canvas.getContext('webgl2') ??
      canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

    if (!gl) {
      return {
        signals: { available: na<boolean>('unavailable', 'client', 'no WebGL context') },
      };
    }

    // --- 1. rendered-pixel fingerprint ------------------------------------
    let renderHash: string | null = null;
    let renderError: string | null = null;
    try {
      renderHash = await renderAndHash(gl);
    } catch (e) {
      renderError = String((e as Error)?.message ?? e);
    }

    // --- 2. shader language translation -----------------------------------
    const shaderLang = detectTranslatedShaderLang(gl);

    return {
      signals: {
        available: ok(true, 'client'),
        // Hash of the rendered RGBA bytes. Stable per real pipeline.
        renderHash: ok(renderHash, 'client'),
        renderError: ok(renderError, 'client'),
        // 'glsl' when the driver receives GLSL verbatim; 'translated' when the
        // browser rewrote it (ANGLE on D3D/Metal).
        shaderLanguage: ok(shaderLang.language, 'client'),
        translated: ok(shaderLang.translated, 'derived'),
        shaderSampleHead: ok(shaderLang.head, 'client'),
        webgl_debug_shaders: ok(!!gl.getExtension('WEBGL_debug_shaders'), 'client'),
      },
      parts: {
        gpu: {
          // The pixel hash is a genuine pipeline identity, so it belongs in the
          // part. The shader-language flag is a backend identity too.
          renderHash,
          translatedShaderLang: shaderLang.translated ? 'translated' : 'glsl',
        },
      },
    };
  },
};

/**
 * Render a gradient + text-free geometry and hash the exact pixels.
 * We avoid text so the result depends on the rasterizer/maths, not the font
 * stack (which the fonts probe already covers).
 */
async function renderAndHash(gl: WebGLRenderingContext): Promise<string> {
  const vs = compile(gl, gl.VERTEX_SHADER, `
    attribute vec2 p;
    void main() {
      // A smooth parabola, so interpolation and the rasterizer's rounding both
      // contribute measurable, implementation-specific output.
      float x = p.x * 0.5 + 0.5;
      float y = p.y * p.y * 0.5 + 0.5;
      gl_Position = vec4(x * 2.0 - 1.0, y * 2.0 - 1.0, 0.0, 1.0);
    }
  `);
  const fs = compile(gl, gl.FRAGMENT_SHADER, `
    precision mediump float;
    void main() {
      float t = gl_FragCoord.x / 200.0;
      gl_FragColor = vec4(t, 1.0 - t, fract(t * 7.0), 1.0);
    }
  `);

  const program = gl.createProgram();
  if (!program) throw new Error('createProgram failed');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`link failed: ${gl.getProgramInfoLog(program)}`);
  }
  gl.useProgram(program);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW,
  );
  const loc = gl.getAttribLocation(program, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  gl.viewport(0, 0, 200, 60);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.finish();

  const pixels = new Uint8Array(200 * 60 * 4);
  gl.readPixels(0, 0, 200, 60, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

  return fnv1aHex(hex(pixels));
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error('createShader failed');
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    throw new Error(`compile failed: ${gl.getShaderInfoLog(sh)}`);
  }
  return sh;
}

function detectTranslatedShaderLang(gl: WebGLRenderingContext): {
  language: 'glsl' | 'translated' | 'unavailable';
  translated: boolean;
  head: string | null;
} {
  const ext = gl.getExtension('WEBGL_debug_shaders') as
    | { getTranslatedShaderSource?: (sh: WebGLShader) => string }
    | null;
  if (!ext?.getTranslatedShaderSource) {
    return { language: 'unavailable', translated: false, head: null };
  }

  let sh: WebGLShader;
  try {
    sh = compile(gl, gl.FRAGMENT_SHADER, 'precision mediump float; void main(){ gl_FragColor=vec4(1.0); }');
  } catch {
    return { language: 'unavailable', translated: false, head: null };
  }

  try {
    const src = ext.getTranslatedShaderSource(sh);
    if (!src) return { language: 'unavailable', translated: false, head: null };
    const head = src.slice(0, 200);
    // ANGLE rewrites the source and marks it with a version comment naming the
    // target (D3D11 / Vulkan / Metal), or uses the HLSL/MSL extension headers.
    const translated =
      /#version\s+300\s+es/.test(src) === false ||
      /d3d|metal|vulkan|hlsl|spir-v|spirv/i.test(src);
    return { language: translated ? 'translated' : 'glsl', translated, head };
  } catch {
    return { language: 'unavailable', translated: false, head: null };
  }
}

function hex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

async function fnv1aHex(input: string): Promise<string> {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 = (Math.imul(h2 ^ c, 0x85ebca6b) >>> 0) ^ (h2 >>> 13);
  }
  return (h1.toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0')).slice(0, 16);
}

registerProbe(webglRenderProbe);
